// ---------------------------------------------------------------------------
// Reads an EvalHid project (.ehd, Access/Jet database) with mdb-reader (pure
// JS, works in the browser — no native driver, no password: confirmed
// against a real project, see CLAUDE.md).
//
// Compatibility with v1.0 projects ("no zones"): before the Zone concept
// existed, each sub-basin had its own parameters/state variables directly —
// APO_PARAMETROS/APO_VAR_ESTADO were indexed by CODIGO_CUENCA, not by
// COD_ZONA (APO_ZONAS, APO_ACUIFEROS_OPC and APO_UNIDADES didn't exist;
// APO_CUENCAS had no COD_ZONA_CUENCA/COD_ZONA_NIEVE or DESCRIPCION —
// confirmed by comparing a real v1.0 .ehd, from
// ../ejemplos/EvalHidResuelto.zip, against the v2 ModeloTuria fixture).
// Detected by the absence of the COD_ZONA column in those two tables, and
// normalized on the fly: each sub-basin synthesizes up to TWO of its own
// zones (see groupZonesV1 below), each only if the corresponding model
// really exists — the rainfall-runoff one with zoneCode=subBasinCode, the
// snow one (if the sub-basin has snowModelTypeCode) with
// zoneCode=-subBasinCode and name "<sub-basin>-snow" (never collides, in
// v1.0 there are no real zones to overlap with). Sub-basins that share a
// model and all their parameter/state variable values are grouped into a
// single shared zone instead of one per sub-basin — so the rest of the app
// (engine.ts, the zones UI...) doesn't need to know the project is v1.0;
// only mapParameterValues/mapStateVariableValues (below) know about this
// sign, to keep finding the real values (indexed by CODIGO_CUENCA plain in
// the original DB, with no rainfall/snow distinction other than their own
// TIPO_MODELO). AquaTool's database connection (the "Modelos" table,
// COD_AQUATOOL columns) isn't touched: it was already ignored in v2 too,
// there was never a live connection, just these static unused columns.
// ---------------------------------------------------------------------------
import { Buffer } from "buffer";
import MDBReader from "mdb-reader";
import type { Translate } from "../i18n/translate.ts";
import { buildUnitOptions, DEFAULT_UNIT_OPTIONS } from "./units.ts";
import type {
  Aquifer,
  SimulationFile,
  GisLayerRow,
  SubBasin,
  ParameterDefinition,
  StateVariableDefinition,
  ScenarioElement,
  EvalHidProject,
  RawRow,
  UnitOption,
  OutletPoint,
  ProjectDateRange,
  ModelType,
  ParameterValue,
  StateVariableValue,
  ProjectVersion,
  Zone,
} from "./types.ts";

/**
 * Names of the already-typed tables; the rest stay in untypedTables.
 * "Modelos" (SIMGES, OPTIGES...) is deliberately left out: it's AQUATOOL's
 * catalog, not our concern (see the note in types.ts).
 */
const TYPED_TABLES = new Set([
  "APO_CUENCAS",
  "APO_ACUIFEROS",
  "APO_PUNTOS_DESAGUE",
  "APO_ZONAS",
  "APO_TIPO_MODELO",
  "APO_DEF_PARAMETROS",
  "APO_PARAMETROS",
  "APO_DEF_VAR_ESTADO",
  "APO_VAR_ESTADO",
  "APO_ARCHIVOS_SIMULACION",
  "GisCapas",
  "APO_FECHA",
  "TAuxEscElem",
  "Version",
  "APO_UNIDADES",
]);

function rows(db: MDBReader, table: string): RawRow[] {
  try {
    return db.getTable(table).getData() as RawRow[];
  } catch {
    return [];
  }
}

function N(v: unknown): number {
  return typeof v === "number" ? v : Number(v ?? 0);
}
function NN(v: unknown): number | null {
  return v === null || v === undefined ? null : Number(v);
}
function S(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}
function SN(v: unknown): string | null {
  return v === null || v === undefined ? null : String(v);
}

/**
 * `namePerElement`: name fallback for when APO_CUENCAS.DESCRIPCION is
 * missing (a column that doesn't exist in v1.0 projects, see file header) —
 * uses TAuxEscElem.NomElem, the same source the real .exe itself uses for
 * the sub-basin name when exporting Ent_Cuencas.csv (confirmed in
 * Form1.vb/WriteArchivoCuencas), so it's not just a compatibility patch.
 */
function mapSubBasins(
  rowsIn: RawRow[],
  namePerElement: Map<number, string>,
  t: Translate
): SubBasin[] {
  return rowsIn.map((r) => {
    const subBasinCode = N(r.CODIGO_CUENCA);
    const description = S(r.DESCRIPCION) || namePerElement.get(subBasinCode) || "";
    return {
      subBasinCode,
      area: N(r.SUPERFICIE_CUENCA),
      precipCol: N(r.COL_PRECIP),
      etpCol: N(r.COL_EVAPO),
      tempCol: N(r.COL_TEMP),
      partialResults: N(r.RES_PARCIALES),
      precipAdjustment: N(r.AJ_PRECIP),
      etpAdjustment: N(r.AJ_ETP),
      tempAdjustment: N(r.AJ_TEMP),
      modelTypeCode: N(r.TIPO_MODELO_CUENCA),
      outletCode: N(r.COD_DESAGUE_SUP) || null,
      aquiferCode: N(r.COD_ACUIF_DESAGUE),
      snowModelTypeCode: N(r.TIPO_MODELO_NIEVE),
      gisCode: NN(r.COD_GIS),
      description: description || t("ehd.subBasinFallbackName", { n: subBasinCode }),
      zoneCode: NN(r.COD_ZONA_CUENCA),
      snowZoneCode: NN(r.COD_ZONA_NIEVE),
      // No real .ehd column for this (same as Aquifer.pumpingCol below) —
      // resolved by name against the loaded pumping series when the project
      // is opened, see project.ts's resolveIfUnlinked.
      pumpingCol: 0,
    };
  });
}

function mapAquifers(rowsIn: RawRow[]): Aquifer[] {
  return rowsIn.map((r) => ({
    aquiferCode: N(r.cod_acuifero),
    name: S(r.Nombre_acuifero),
    gisCode: NN(r.COD_GIS),
    aquatoolCode: NN(r.COD_AQUATOOL),
    outletCode: NN(r.Cod_puntos_desague),
    vo: NN(r.Vo),
    alfa: NN(r.Alfa),
    // The .ehd doesn't bring any link column for Pumping — resolved by name
    // when opening the project (see resolveIndicesByName, project.ts).
    pumpingCol: 0,
  }));
}

function mapOutletPoints(rowsIn: RawRow[]): OutletPoint[] {
  return rowsIn.map((r) => ({
    outletCode: N(r.Cod_puntos_desague),
    name: S(r.Nombre_puntos_desague),
    gisCode: NN(r.COD_GIS),
    aquatoolCode: NN(r.COD_AQUATOOL),
  }));
}

function mapZones(rowsIn: RawRow[]): Zone[] {
  return rowsIn.map((r) => ({
    zoneCode: N(r.COD_ZONA),
    name: S(r.Nom_Zona),
    modelTypeCode: NN(r.Tipo_Modelo_Cuenca),
    snowModelTypeCode: NN(r.Tipo_Modelo_Nieve),
  }));
}

function mapModelTypes(rowsIn: RawRow[]): ModelType[] {
  return rowsIn.map((r) => ({
    modelTypeCode: N(r.CODIGO_TIPO_MODELO),
    modelTypeName: S(r.NOMBRE_TIPO_MODELO),
    kind: N(r.TIPO_MODELO),
  }));
}

function mapParameterDefinitions(rowsIn: RawRow[]): ParameterDefinition[] {
  return rowsIn.map((r) => ({
    parameterCode: N(r.CODIGO_PARAMETRO),
    name: S(r.NOMBRE_PARAMETRO),
    modelTypeCode: N(r.CODIGO_TIPO_MODELO),
    kind: N(r.TIPO_MODELO),
    defaultValue: NN(r.VALORES_DEFECTO),
    units: SN(r.UNIDADES),
    longName: SN(r.NOMBRE_LARGO),
  }));
}

/**
 * `zoneCodeKey`: in v1.0 projects ("no zones") APO_PARAMETROS/APO_VAR_ESTADO
 * are indexed by CODIGO_CUENCA, not COD_ZONA — see file header. In that
 * case, snow rows (TIPO_MODELO=1) also use a negative zoneCode:
 * `groupZonesV1()` (called from loadEhd()'s `noZones` block) synthesizes a
 * snow zone SEPARATE from the rainfall-runoff one (same id, negative, never
 * colliding), and this has to mirror that same convention so that
 * parametrosDeZona() (engine.ts) keeps finding the real values. Shared by
 * mapParameterValues/mapStateVariableValues below — same table shape,
 * different columns.
 */
function resolveZoneCode(
  r: RawRow,
  zoneCodeKey: "COD_ZONA" | "CODIGO_CUENCA",
  kind: number
): number {
  const rawZoneCode = N(r[zoneCodeKey]);
  return zoneCodeKey === "CODIGO_CUENCA" && kind === 1 ? -rawZoneCode : rawZoneCode;
}

function mapParameterValues(
  rowsIn: RawRow[],
  zoneCodeKey: "COD_ZONA" | "CODIGO_CUENCA"
): ParameterValue[] {
  return rowsIn.map((r) => {
    const kind = N(r.TIPO_MODELO);
    return {
      zoneCode: resolveZoneCode(r, zoneCodeKey, kind),
      parameterCode: N(r.CODIGO_PARAMETRO),
      kind,
      value: NN(r.VALOR_PARAMETRO),
    };
  });
}

function mapStateVariableDefinitions(rowsIn: RawRow[]): StateVariableDefinition[] {
  return rowsIn.map((r) => ({
    stateVariableCode: N(r.CODIGO_VARIABLE_ESTADO),
    name: S(r.NOMBRE_VARIABLE_ESTADO),
    modelTypeCode: N(r.CODIGO_TIPO_MODELO),
    kind: N(r.TIPO_MODELO),
    defaultValue: NN(r.VALORES_DEFECTO),
    units: SN(r.UNIDADES),
    longName: SN(r.NOMBRE_LARGO),
  }));
}

/** `zoneCodeKey`: same convention as mapParameterValues — see resolveZoneCode's comment. */
function mapStateVariableValues(
  rowsIn: RawRow[],
  zoneCodeKey: "COD_ZONA" | "CODIGO_CUENCA"
): StateVariableValue[] {
  return rowsIn.map((r) => {
    const kind = N(r.TIPO_MODELO);
    return {
      zoneCode: resolveZoneCode(r, zoneCodeKey, kind),
      stateVariableCode: N(r.CODIGO_VARIABLE_ESTADO),
      kind,
      value: NN(r.VALOR_VARIABLE_ESTADO),
    };
  });
}

function mapSimulationFiles(rowsIn: RawRow[]): SimulationFile[] {
  return rowsIn.map((r) => ({
    modelCode: N(r.CodModelo),
    label: S(r.ETIQUETA),
    path: S(r.RUTA),
    fileCode: N(r.CodArchivo),
    extra: SN(r.EXTRA),
  }));
}

function mapGisLayers(rowsIn: RawRow[]): GisLayerRow[] {
  return rowsIn.map((r) => ({
    scenarioCode: N(r.CodEscenario),
    layer: S(r.Capa),
    group: S(r.Grupo),
    order: N(r.Orden),
    shapefile: S(r.Shapefile),
    idField: SN(r.CampoID),
    labelField: SN(r.CampoEtiqueta),
    waterBodyTypeCode: N(r.CodTipoMasa),
    color: N(r.Color),
    typeCode: N(r.CodTipo),
    idCode: SN(r.CodID),
    nameCode: SN(r.CodNom),
    areaCode: SN(r.CodArea),
  }));
}

function mapDateRanges(rowsIn: RawRow[]): ProjectDateRange[] {
  return rowsIn.map((r) => ({
    id: N(r.Id),
    dateFormat: N(r.FORMATO_FECHA),
    accumulation: N(r.ACUMULACION),
    startDate: N(r.FECHA_INI),
    endDate: N(r.FECHA_FIN),
  }));
}

function mapScenarioElements(rowsIn: RawRow[]): ScenarioElement[] {
  return rowsIn.map((r) => ({
    scenarioCode: N(r.CodEscenario),
    elementCode: N(r.CodElemento),
    elementTypeCode: N(r.CodTipoElem),
    name: S(r.NomElem),
    originNode: NN(r.NudoOrigen),
    destinationNode: NN(r.NudoDestino),
    userNumber: NN(r.NumUser),
  }));
}

/** Absent in v1.0 projects (see file header) and in any .ehd that never opened the original's "Tools" tab — falls back to the MPE_APO_base.sehd seed (active km²). */
function mapUnitOptions(rowsIn: RawRow[]): UnitOption[] {
  if (rowsIn.length === 0) return DEFAULT_UNIT_OPTIONS;
  return buildUnitOptions(
    rowsIn.map((r) => ({
      code: N(r.Codigo),
      checked: Boolean(r.Checked),
      units: S(r.Unidades),
      value: N(r.Valor),
    }))
  );
}

/**
 * Signature of a sub-basin's explicit values for a given kind (0
 * rainfall-runoff, 1 snow) — two sub-basins with the same signature have
 * exactly the same parameters and state variables (comparing what's really
 * in APO_PARAMETROS/APO_VAR_ESTADO, not against their definition's default
 * values). `zoneCode` must already come with the snow sign applied, the way
 * mapParameterValues/mapStateVariableValues leave it with
 * zoneCodeKey="CODIGO_CUENCA" (zoneCode=subBasinCode for kind=0,
 * zoneCode=-subBasinCode for kind=1).
 */
function valueSignature(
  parameterValues: ParameterValue[],
  stateVariableValues: StateVariableValue[],
  zoneCode: number,
  kind: number
): string {
  const params = parameterValues
    .filter((v) => v.kind === kind && v.zoneCode === zoneCode)
    .map((v) => `${v.parameterCode}:${v.value}`)
    .sort()
    .join(",");
  const vars = stateVariableValues
    .filter((v) => v.kind === kind && v.zoneCode === zoneCode)
    .map((v) => `${v.stateVariableCode}:${v.value}`)
    .sort()
    .join(",");
  return `${params}|${vars}`;
}

/**
 * Groups the sub-basins of a v1.0 project ("no zones", see file header)
 * that share a model and all their parameter/state variable values into a
 * single shared zone, instead of synthesizing one zone per sub-basin even
 * if identical to others — a real project with dozens of identically
 * calibrated sub-basins ended up with dozens of zones, unmanageable in the
 * zones UI (Step2Zones.tsx). Rainfall-runoff and snow are grouped
 * separately: two sub-basins can share a rainfall-runoff zone without
 * sharing the snow one, or vice versa. The first sub-basin of each group (in
 * `subBasins` order) acts as the representative — its subBasinCode/name are
 * used for the shared zone, and the rest of the group's sub-basins point to
 * that same zoneCode. If the automatic grouping isn't what's wanted, more
 * zones can still be created and sub-basins reassigned by hand afterward
 * (assignZoneInBulk, projectEntities.ts).
 */
export function groupZonesV1(
  subBasins: SubBasin[],
  parameterValues: ParameterValue[],
  stateVariableValues: StateVariableValue[],
  t: Translate
): {
  subBasins: SubBasin[];
  zones: Zone[];
  parameterValues: ParameterValue[];
  stateVariableValues: StateVariableValue[];
} {
  const runoffReps = new Map<string, number>();
  const snowReps = new Map<string, number>();
  const subBasinZone = new Map<number, number | null>();
  const subBasinSnowZone = new Map<number, number | null>();

  for (const c of subBasins) {
    if (c.modelTypeCode) {
      const key = `${c.modelTypeCode}|${valueSignature(parameterValues, stateVariableValues, c.subBasinCode, 0)}`;
      if (!runoffReps.has(key)) runoffReps.set(key, c.subBasinCode);
      subBasinZone.set(c.subBasinCode, runoffReps.get(key)!);
    } else {
      subBasinZone.set(c.subBasinCode, null);
    }
    if (c.snowModelTypeCode) {
      const key = `${c.snowModelTypeCode}|${valueSignature(parameterValues, stateVariableValues, -c.subBasinCode, 1)}`;
      if (!snowReps.has(key)) snowReps.set(key, c.subBasinCode);
      subBasinSnowZone.set(c.subBasinCode, -snowReps.get(key)!);
    } else {
      subBasinSnowZone.set(c.subBasinCode, null);
    }
  }

  const runoffRepresentatives = new Set(runoffReps.values());
  const snowRepresentatives = new Set(snowReps.values());

  // Once grouped, the representative sub-basin's name no longer describes
  // the zone well (it may cover several sub-basins with different names) —
  // numbered "Zone 1", "Zone 2"... instead of inheriting one of their names.
  let zoneNumber = 0;
  const zones: Zone[] = subBasins.flatMap((c) => {
    const own: Zone[] = [];
    if (runoffRepresentatives.has(c.subBasinCode)) {
      own.push({
        zoneCode: c.subBasinCode,
        name: t("ehd.zoneFallbackName", { n: ++zoneNumber }),
        modelTypeCode: c.modelTypeCode,
        snowModelTypeCode: null,
      });
    }
    if (snowRepresentatives.has(c.subBasinCode)) {
      own.push({
        zoneCode: -c.subBasinCode,
        name: t("ehd.snowZoneFallbackName", { n: ++zoneNumber }),
        modelTypeCode: null,
        snowModelTypeCode: c.snowModelTypeCode,
      });
    }
    return own;
  });

  return {
    subBasins: subBasins.map((c) => ({
      ...c,
      zoneCode: subBasinZone.get(c.subBasinCode) ?? null,
      snowZoneCode: subBasinSnowZone.get(c.subBasinCode) ?? null,
    })),
    zones,
    parameterValues: parameterValues.filter(
      (v) =>
        (v.kind === 0 && runoffRepresentatives.has(v.zoneCode)) ||
        (v.kind === 1 && snowRepresentatives.has(-v.zoneCode))
    ),
    stateVariableValues: stateVariableValues.filter(
      (v) =>
        (v.kind === 0 && runoffRepresentatives.has(v.zoneCode)) ||
        (v.kind === 1 && snowRepresentatives.has(-v.zoneCode))
    ),
  };
}

function mapVersion(rowsIn: RawRow[]): ProjectVersion | null {
  const r = rowsIn[0];
  if (!r) return null;
  return {
    database: N(r.BaseDatos),
    simges: NN(r.Simges),
    gescal: NN(r.Gescal),
    dataImporter: NN(r.ImportadorDatos),
  };
}

/** Loads an EvalHid project from the bytes of its .ehd file. */
export function loadEhd(bytes: ArrayBuffer | Uint8Array, t: Translate): EvalHidProject {
  const buf = Buffer.from(bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes);
  const db = new MDBReader(buf);

  const untypedTables = new Map<string, RawRow[]>();
  for (const name of db.getTableNames()) {
    if (!TYPED_TABLES.has(name)) {
      untypedTables.set(name, rows(db, name));
    }
  }

  const parameterRows = rows(db, "APO_PARAMETROS");
  const stateVariableRows = rows(db, "APO_VAR_ESTADO");
  // v1.0 ("no zones"): these two tables are indexed by CODIGO_CUENCA, not by
  // COD_ZONA (the column simply doesn't exist) — see file header.
  const sampleRow = parameterRows[0] ?? stateVariableRows[0];
  const noZones = sampleRow != null && !("COD_ZONA" in sampleRow);
  const zoneCodeKey = noZones ? "CODIGO_CUENCA" : "COD_ZONA";

  const scenarioElements = mapScenarioElements(rows(db, "TAuxEscElem"));
  const namePerElement = new Map(scenarioElements.map((e) => [e.elementCode, e.name]));
  let subBasins = mapSubBasins(rows(db, "APO_CUENCAS"), namePerElement, t);
  let zones = mapZones(rows(db, "APO_ZONAS"));
  let parameterValues = mapParameterValues(parameterRows, zoneCodeKey);
  let stateVariableValues = mapStateVariableValues(stateVariableRows, zoneCodeKey);

  if (noZones) {
    ({ subBasins, zones, parameterValues, stateVariableValues } = groupZonesV1(
      subBasins,
      parameterValues,
      stateVariableValues,
      t
    ));
  }

  return {
    subBasins,
    aquifers: mapAquifers(rows(db, "APO_ACUIFEROS")),
    outletPoints: mapOutletPoints(rows(db, "APO_PUNTOS_DESAGUE")),
    zones,
    modelTypes: mapModelTypes(rows(db, "APO_TIPO_MODELO")),
    parameterDefinitions: mapParameterDefinitions(rows(db, "APO_DEF_PARAMETROS")),
    parameterValues,
    stateVariableDefinitions: mapStateVariableDefinitions(rows(db, "APO_DEF_VAR_ESTADO")),
    stateVariableValues,
    simulationFiles: mapSimulationFiles(rows(db, "APO_ARCHIVOS_SIMULACION")),
    gisLayers: mapGisLayers(rows(db, "GisCapas")),
    dateRanges: mapDateRanges(rows(db, "APO_FECHA")),
    scenarioElements,
    version: mapVersion(rows(db, "Version")),
    unitOptions: mapUnitOptions(rows(db, "APO_UNIDADES")),
    // The real .ehd has no equivalent field to read here (Escala_Temporal
    // only exists in Ent_Config.csv, the desktop CSV contract — never
    // stored in the .ehd itself) — defaults to "daily" and gets corrected,
    // if needed, once the real precipitation series loads (see
    // project.ts's own detectScale step, same bootstrap the CSV-only
    // import path uses).
    seriesScale: "daily",
    untypedTables,
  };
}
